import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, queryCache } from "react-query";
import { AxiosError } from "axios";

import { ApiError } from "fe-shared/src/types/common";

import useDialog from "fe-shared/src/hooks/useDialog";
import { Button } from "fe-shared/src/components";
import { TransactionsIcon } from "fe-shared/src/components/Icons";
import { BusinessRole } from "fe-shared/src/types/domain";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import { TRANSACTIONS_QUERY_KEY } from "../../consts";
import { RetreadingTransactionFormContent } from "../../types";

import RetreadingTransactionFormDialog from "../RetreadingTransactionFormDialog";

import * as api from "./modules/api";

const CreateRetreadingTransaction = () => {
  const { t } = useTranslation();
  const {
    data: materials,
    isLoading: isMaterialsLoading,
  } = useRecycleMaterials([BusinessRole.Retreader]);

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isDialogActive) closeDialog();
    };

    if (isDialogActive) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (isDialogActive) {
        window.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [closeDialog, isDialogActive]);

  const [
    addRetreadingTransaction,
    addRetreadingTransactionResult,
  ] = useMutation<
    unknown,
    AxiosError<ApiError>,
    RetreadingTransactionFormContent
  >(api.addRetreadingTransaction, {
    onSuccess: () => {
      closeDialog();
      queryCache.invalidateQueries(TRANSACTIONS_QUERY_KEY);
    },
  });

  const onSubmit = useCallback(
    (values: RetreadingTransactionFormContent) => {
      addRetreadingTransaction(values);
    },
    [addRetreadingTransaction]
  );
  const {
    isLoading: isCreatingTransacitonLoading,
  } = addRetreadingTransactionResult;
  const isLoading = isMaterialsLoading || isCreatingTransacitonLoading;

  return (
    <>
      <Button
        id="crete-retreading-transaction-button"
        key="crete-retreading-transaction-button"
        iconComponent={TransactionsIcon}
        variant="inline"
        onClick={() => openDialog()}
      >
        {t("transactions.grid.createRetreadingTransaction.button")}
      </Button>
      {isDialogRendered && (
        <RetreadingTransactionFormDialog
          title={t("transactions.grid.createRetreadingTransaction.title")}
          isActive={isDialogActive}
          onClose={closeDialog}
          onSubmit={onSubmit}
          isLoading={isLoading}
          materials={materials || []}
        />
      )}
    </>
  );
};

export default CreateRetreadingTransaction;
